import { useState } from "react";
import { ArrowLeft, Loader2 } from "lucide-react";
import { registerKitchenStaff } from "@/lib/kitchenStaff";

const PINK = "#EE2677";

const ROLES = [
  { id: "baker", label: "Baker" },
  { id: "froster", label: "Froster" },
  { id: "decorator", label: "Decorator" },
];

/** One-time kitchen team sign-up — name + phone + usual station. */
export default function KitchenStaffOnboard({ onComplete, onCancel, initialPhone = "" }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState(initialPhone);
  const [primaryRole, setPrimaryRole] = useState("froster");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const data = await registerKitchenStaff({ name, phone, primaryRole });
      onComplete?.(data);
    } catch (ex) {
      setErr(ex.message || String(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ka-staff-onboard">
      <style>{`
        .ka-staff-onboard {
          min-height: 100dvh; background: #F5F2EA; color: #2D2D2D;
          padding: 12px 14px 28px; max-width: 480px; margin: 0 auto;
          font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
        }
        .ka-staff-onboard * { box-sizing: border-box; }
        .ka-staff-brand { text-align: center; margin-bottom: 18px; }
        .ka-staff-brand-kicker {
          font-size: 11px; font-weight: 800; letter-spacing: 0.12em;
          text-transform: uppercase; color: rgba(45,45,45,0.45); margin: 0 0 4px;
        }
        .ka-staff-brand-name { font-size: 28px; font-weight: 900; color: ${PINK}; margin: 0; }
        .ka-staff-head { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; }
        .ka-staff-back {
          border: none; background: #fff; border-radius: 12px; width: 40px; height: 40px;
          display: flex; align-items: center; justify-content: center; cursor: pointer;
          box-shadow: 0 1px 4px rgba(0,0,0,0.08);
        }
        .ka-staff-title { font-size: 20px; font-weight: 800; margin: 0; }
        .ka-staff-sub { font-size: 14px; color: rgba(45,45,45,0.65); margin: 6px 0 0; line-height: 1.45; }
        .ka-staff-field { margin-bottom: 12px; }
        .ka-staff-field label { display: block; font-size: 13px; font-weight: 700; margin-bottom: 6px; }
        .ka-staff-field input {
          width: 100%; border: 2px solid #E5E7EB; border-radius: 14px;
          padding: 12px 14px; font-size: 16px; background: #fff;
        }
        .ka-staff-roles { display: flex; gap: 8px; flex-wrap: wrap; }
        .ka-staff-role {
          flex: 1; min-width: 90px; border: 2px solid #E5E7EB; border-radius: 12px;
          padding: 10px 8px; background: #fff; font-size: 12px; font-weight: 800; cursor: pointer;
        }
        .ka-staff-role.on { border-color: ${PINK}; color: ${PINK}; background: #FFF7FA; }
        .ka-staff-primary {
          width: 100%; border: none; border-radius: 16px; padding: 16px;
          font-size: 17px; font-weight: 800; color: #fff; background: ${PINK}; cursor: pointer; margin-top: 8px;
        }
        .ka-staff-primary:disabled { opacity: 0.6; cursor: wait; }
        .ka-staff-err { color: #B91C1C; font-size: 14px; font-weight: 600; margin: 8px 0; }
      `}</style>

      <div className="ka-staff-brand">
        <p className="ka-staff-brand-kicker">Kitchen app</p>
        <h2 className="ka-staff-brand-name">The Table Top</h2>
      </div>

      <div className="ka-staff-head">
        {onCancel ? (
          <button type="button" className="ka-staff-back" onClick={onCancel} aria-label="Back">
            <ArrowLeft size={20} />
          </button>
        ) : null}
        <div>
          <h1 className="ka-staff-title">Team sign-up</h1>
          <p className="ka-staff-sub">Ek baar naam aur mobile daalo — baad mein sirf number se login.</p>
        </div>
      </div>

      <form onSubmit={submit}>
        <div className="ka-staff-field">
          <label htmlFor="ks-name">Full name</label>
          <input id="ks-name" value={name} onChange={(e) => setName(e.target.value)} required autoComplete="name" />
        </div>
        <div className="ka-staff-field">
          <label htmlFor="ks-phone">Mobile number</label>
          <input
            id="ks-phone"
            type="tel"
            inputMode="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            required
            autoComplete="tel"
            placeholder="10-digit mobile"
          />
        </div>
        <div className="ka-staff-field">
          <label>Main station (optional)</label>
          <div className="ka-staff-roles">
            {ROLES.map((r) => (
              <button
                key={r.id}
                type="button"
                className={`ka-staff-role${primaryRole === r.id ? " on" : ""}`}
                onClick={() => setPrimaryRole(r.id)}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
        {err ? <p className="ka-staff-err">{err}</p> : null}
        <button type="submit" className="ka-staff-primary" disabled={busy}>
          {busy ? <Loader2 className="animate-spin" size={18} style={{ verticalAlign: "middle" }} /> : null}
          {busy ? " Creating account…" : "Sign up & start shift"}
        </button>
      </form>
    </div>
  );
}
